import { FormEvent } from 'react';
import { Loader2, Users, X } from 'lucide-react';

import InputField from '../../../components/Forms/Input/InputField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';

import {
  ReservationAvailability,
  ReservationFieldErrors,
  ReservationFormState,
} from '../../../types/restaurant/RestaurantReservationTypes';

import { RestaurantPartyOption } from '../../../types/restaurant/RestaurantOrderTypes';
import TimePicker from '../../../components/Forms/Time/TimePicker';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';

interface Props {
  open: boolean;
  form: ReservationFormState;
  errors: ReservationFieldErrors;
  customers: RestaurantPartyOption[];
  availability: ReservationAvailability[];
  availabilityLoading: boolean;
  saving: boolean;
  allowGuests: boolean;
  allowMultipleTables: boolean;
  allowOverbooking: boolean;
  maximumAdvanceDays: number;
  selectedCapacity: number;
  onClose: () => void;
  onChange: <K extends keyof ReservationFormState>(
    field: K,
    value: ReservationFormState[K]
  ) => void;
  onSubmit: () => void;
}

const ReservationFormModal = ({
  open,
  form,
  errors,
  customers,
  availability,
  availabilityLoading,
  saving,
  allowGuests,
  allowMultipleTables,
  allowOverbooking,
  maximumAdvanceDays,
  selectedCapacity,
  onClose,
  onChange,
  onSubmit,
}: Props) => {
  if (!open) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const toggleTable = (tableId: number) => {
    const selected = form.tableIds.includes(tableId);

    if (selected) {
      onChange(
        'tableIds',
        form.tableIds.filter((id) => id !== tableId)
      );
      return;
    }

    onChange(
      'tableIds',
      allowMultipleTables ? [...form.tableIds, tableId] : [tableId]
    );
  };

  const customerOptions = customers.map((customer) => ({
    id: customer.id,
    name: customer.name,
  }));

  return (
    <div
      className="fixed inset-0 z-99999 flex items-center justify-center bg-black/55 p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reservation-form-title"
    >
      <form
        onSubmit={submit}
        className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-default dark:bg-boxdark"
      >
        <div className="flex items-start justify-between border-b border-stroke p-5 dark:border-strokedark sm:px-7">
          <div>
            <h2
              id="reservation-form-title"
              className="text-xl font-semibold text-black dark:text-white"
            >
              {form.id ? 'Editar reservación' : 'Nueva reservación'}
            </h2>

            <p className="mt-1 text-sm text-bodydark2">
              Los horarios se guardan en la hora local del negocio.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <RestaurantFormHelp topic="reservation" variant="inline" />

            <button
              type="button"
              aria-label="Cerrar"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-gray dark:hover:bg-boxdark-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 sm:p-7">
          <section>
            <h3 className="font-semibold text-black dark:text-white">
              Cliente
            </h3>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => onChange('customerMode', 'REGISTERED')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  form.customerMode === 'REGISTERED'
                    ? 'bg-primary text-white'
                    : 'bg-gray-2 text-body dark:bg-boxdark-2'
                }`}
              >
                Registrado
              </button>

              {allowGuests && (
                <button
                  type="button"
                  onClick={() => onChange('customerMode', 'GUEST')}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${
                    form.customerMode === 'GUEST'
                      ? 'bg-primary text-white'
                      : 'bg-gray-2 text-body dark:bg-boxdark-2'
                  }`}
                >
                  Invitado
                </button>
              )}
            </div>

            {form.customerMode === 'REGISTERED' ? (
              <div className="mt-4">
                <SimpleSearchSelect
                  label="Cliente"
                  name="customerId"
                  placeholder="Seleccione un cliente"
                  searchPlaceholder="Buscar cliente..."
                  iconKey="user"
                  required
                  options={customerOptions}
                  selectedValue={
                    form.customerId ? Number(form.customerId) : null
                  }
                  onSelect={(customer) => onChange('customerId', customer.id)}
                  error={errors.customerId}
                />
              </div>
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <InputField
                  label="Nombre"
                  name="guestName"
                  type="text"
                  value={form.guestName}
                  required
                  iconKey="user"
                  error={errors.guestName}
                  onChange={(event) =>
                    onChange('guestName', event.target.value)
                  }
                />

                <InputField
                  label="Teléfono"
                  name="guestPhone"
                  type="text"
                  value={form.guestPhone}
                  iconKey="phone"
                  error={errors.guestPhone}
                  onChange={(event) =>
                    onChange('guestPhone', event.target.value)
                  }
                />

                <InputField
                  label="Correo"
                  name="guestEmail"
                  type="email"
                  value={form.guestEmail}
                  iconKey="email"
                  error={errors.guestEmail}
                  onChange={(event) =>
                    onChange('guestEmail', event.target.value)
                  }
                />
              </div>
            )}
          </section>

          <section className="mt-7 border-t border-stroke pt-6 dark:border-strokedark">
            <h3 className="font-semibold text-black dark:text-white">
              Fecha y grupo
            </h3>

            <p className="mt-1 text-xs text-bodydark2">
              Puede reservar hasta {maximumAdvanceDays} días de anticipación.
            </p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <DatePicker
                label="Fecha"
                name="date"
                value={form.date}
                required
                iconKey="calendar"
                error={errors.date}
                onChange={(date) => onChange('date', date)}
              />

              <TimePicker
                label="Hora"
                name="time"
                value={form.time}
                required
                iconKey="clock"
                error={errors.time}
                onChange={(time) => onChange('time', time)}
              />

              <InputField
                label="Duración (min.)"
                name="durationMinutes"
                type="number"
                value={form.durationMinutes}
                min={15}
                max={1440}
                step="15"
                required
                iconKey="clock"
                error={errors.durationMinutes}
                onChange={(event) =>
                  onChange('durationMinutes', Number(event.target.value))
                }
              />

              <InputField
                label="Personas"
                name="partySize"
                type="number"
                value={form.partySize}
                min={1}
                max={1000}
                required
                iconKey="user"
                error={errors.partySize}
                onChange={(event) =>
                  onChange('partySize', Number(event.target.value))
                }
              />
            </div>
          </section>

          <section className="mt-7 border-t border-stroke pt-6 dark:border-strokedark">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-black dark:text-white">
                  Mesas disponibles
                </h3>

                <p className="mt-1 text-xs text-bodydark2">
                  {allowMultipleTables
                    ? 'Puede combinar varias mesas.'
                    : 'Solo puede seleccionar una mesa.'}
                </p>
              </div>

              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${
                  !allowOverbooking && selectedCapacity < form.partySize
                    ? 'bg-danger/10 text-danger'
                    : 'bg-success/10 text-success'
                }`}
              >
                <Users className="h-4 w-4" />
                Capacidad {selectedCapacity} / {form.partySize}
              </span>
            </div>

            {availabilityLoading ? (
              <div className="flex h-28 items-center justify-center gap-2 text-sm text-bodydark2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                Consultando disponibilidad...
              </div>
            ) : availability.length ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {availability.map((table) => {
                  const selected = form.tableIds.includes(table.tableId);

                  return (
                    <button
                      type="button"
                      key={table.tableId}
                      onClick={() => toggleTable(table.tableId)}
                      aria-pressed={selected}
                      className={`rounded-xl border p-3 text-left transition ${
                        selected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-stroke hover:border-primary/50 dark:border-strokedark'
                      }`}
                    >
                      <span className="block font-semibold text-black dark:text-white">
                        {table.code} · {table.name}
                      </span>

                      <span className="mt-1 block text-xs text-bodydark2">
                        {table.diningRoomName || 'Sin salón'} · {table.capacity}{' '}
                        personas
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="mt-4 rounded-xl bg-warning/10 p-4 text-sm text-meta-8">
                No hay mesas disponibles para este horario.
              </p>
            )}

            {errors.tableIds && (
              <p className="mt-2 text-sm text-danger">{errors.tableIds}</p>
            )}
          </section>

          <div className="mt-6">
            <TextAreaField
              label="Notas"
              name="notes"
              value={form.notes}
              rows={3}
              placeholder="Observaciones de la reservación..."
              iconKey="note"
              onChange={(event) => onChange('notes', event.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-stroke p-5 dark:border-strokedark sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-stroke px-5 py-2.5 text-sm font-medium dark:border-strokedark"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving || availabilityLoading}
            className="inline-flex min-w-[130px] items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {form.id ? 'Guardar cambios' : 'Crear reservación'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReservationFormModal;
